import {
  cascadingGetIfNotEmpty,
  isEmpty,
  isNil,
  notEmpty,
  notNil
} from '@megaphonenpm/mputils/src/utils.js';
import {div, hide, show} from '@megaphonenpm/mputils/src/DOM.js';
import {
  CLIENT_TYPE_SCREEN, PRODUCTNAME_BLANK, PRODUCTNAME_UGC,
  STATE_SCENE_RESULT,
} from '@megaphonenpm/mrtsclient/src/constants.js';
import {
  CHANGE
} from '@megaphonenpm/mputils/src/constants.js';
import {MediaContainer} from "./MediaContainer.js";
import {play} from "./Animation.js";
import {MegaScreen} from "./MegaScreen.js";
import {get} from 'lodash-es';
import {DoubleBufferedMediaContainer} from './DoubleBufferedMediaContainer.js';

/**
 * A view that displays external media as a "full screen" layer ("full screen" here meaing
 * "covers MegaScreen's entire display region," not literally "full screen mode on the computer
 * running MegaScreen"). Used to display foreground and background media for the current scene
 * and playlist/deployment. Automatically retrieves the appropriate media from the cache based
 * on the specified media name.
 *
 * Example:
 * // Displays content.background.media.screen.uri for the scene and "playlist or deployment," if
 * // the background is set in Editor.
 * let backgroundLayer = new MediaView('background');
 */
export class MediaView {
  constructor (name) {
    this.name = name;
    // When true, for playlist and deployment media, the intro animation will be played the first
    // time this media is displayed (once only), NOT at the beginning of each state. Used for
    // background media, to allow, say, a background video to play without interruption behind
    // multiple scenes.
    this.playOnce = false;
    this.visibleStates = null;
    this.init();
  }

  init () {
    // Create top-level container div for this MediaView
    this.view = div(this.name, 'mediaView');

    // Create media container for the scene
    this.media = new DoubleBufferedMediaContainer(`${this.name}Media`);

    // Add children
    this.view.append(this.media.view);
  }

  setAppState (appState) {
    this.appState = appState;
    this.registerForAppStateChanges();
  }

  registerForAppStateChanges () {
    this.appState.on(CHANGE, e => {
      this.syncToState(e.detail);
    });
  }

  syncToState (changedProp) {
    if (isNil(changedProp) || changedProp.prop === 'state') {
      this.startState();
    }
  }

  startState () {
    this.syncVisibility();
    this.syncMedia();
  }

  // TODO: Currently, for MediaView instances ("Sponsor Media", "Show Media", etc), MegaScreen does
  //  not obey the does not read the settings provided by Theme Designer for product-level visibility
  //  or global (default) visibility. Those settings are present in the spec, and should be honored
  //  here. Implement visibility-setting cascade for MediaView instances.
  syncVisibility () {
    this.view.classList.remove('visible_state');
    if (this.isVisibleState()) {
      this.view.classList.add('visible_state');
    }
  }

  /**
   * Returns true if the current MediaView object should be visible during the current
   * state/scene/product/layout. Otherwise, returns false.
   *
   * For any MediaView object, in theme settings, the 'visible' setting can be assigned at four
   * cascading levels:
   * 1) product layout (highest precedence)
   * 2) product
   * 3) state
   * 4) default (lowest precedence)
   *
   * For the 'visible' setting, possible values are true, false, and null.
   *
   * If 'visible' is explicitly set to true at the most specific of the above levels, the current
   * MediaView object should be shown. Otherwise, it should be hidden.
   */
  isVisibleState () {
    let isBlankScene = MegaScreen.globals.sceneService.getScreenProductName(MegaScreen.globals.appState.scene) === PRODUCTNAME_BLANK;

    if (isBlankScene) {
      // Special case: Always hide MediaViews during "Blank" scenes
      return false;
    } else {
      return this.getCascadingThemeSettingValue('visible') === true;
    }
  }

  syncMedia () {
    let sceneMediaURI = MegaScreen.globals.sceneService.getSceneMediaURI(
      MegaScreen.globals.appState.scene,
      this.name,
      CLIENT_TYPE_SCREEN);
    let defaultMediaURI = MegaScreen.globals.cascadingSettingsService.getDefaultMediaURI(
      this.name,
      CLIENT_TYPE_SCREEN,
      MegaScreen.globals.dataStore.playlist,
      MegaScreen.globals.dataStore.deploymentSettings);
    let newURI = notNil(sceneMediaURI) ? sceneMediaURI : defaultMediaURI;

    if (newURI === defaultMediaURI
        && this.media.uri === defaultMediaURI
        && this.playOnce) {
      // When .playOnce is enabled, at the beginning of a new state, if the media is the
      // playlist or deployment media, and the media hasn't changed since the previous
      // state, then don't play this media's intro animation again. Note that .playOnce
      // is NOT applied to scene media (e.g., sponsor media chosen specifically for the current
      // scene). The scene-media intro is ALWAYS played at the beginning of a scene, even if that
      // media is the same as the previous scene.
      return;
    }

    if (this.appState.state === STATE_SCENE_RESULT) {
      if (this.media.uri !== newURI) {
        // If MegaScreen connects during SCENE_RESULT, the scene media won't have been
        // rendered yet, so render it now. Otherwise, don't re-assign the URI because,
        // in the case of a scene video, doing so would restart the video.
        this.initMedia(this.media, newURI);
      }
    } else {
      // All other states
      this.initMedia(this.media, newURI);
    }
  }

  initMedia (media, URI) {
    // Display the new media
    media.setURI(URI).then(() => {
      media.render();
    });

    notNil(URI) ? show(media.view) : hide(media.view);

    // If the media has a layout (size and position) assigned by the current scene, playlist,
    // project, or theme, apply it now.
    let currentThemeSet = MegaScreen.globals.themeService.getCurrentThemeSet();

    let width = this.getSetting('size.width');
    let height = this.getSetting('size.height');

    if (notNil(width) || notNil(height)) {
      this.setSize(width, height);
    }

    let x = this.getSetting('position.x');
    let y = this.getSetting('position.y');

    // If either x or y have an explicit value, position the media. Otherwise, do not set an explicit
    // position (i.e., allow the media to remain at position (0, 0), which handles the background
    // and foreground cases).
    if (notNil(x) || notNil(y)) {
      this.setPosition(get(currentThemeSet, 'layout.screen.position.x', 0) + x,
        get(currentThemeSet, 'layout.screen.position.y', 0) + y);
    }

    // Use CascadingSettingsService directly instead of this.getSetting() because this.getSetting()
    // falls back to the theme setting if the scene, playlist, and project settings are empty. In
    // the case of 'loop', there is no theme-level setting.
    media.setLoop(MegaScreen.globals.cascadingSettingsService.getSettingIfNotEmpty(
    `content.${this.name}.media.screen.loop`,
      MegaScreen.globals.appState.scene,
      MegaScreen.globals.dataStore
    ) === true);

    // If the scene media has an animation specified for the current theme, play it now
    play(this.view,
      this.getSetting('animation.start.label'),
      this.getSetting('animation.start.delay'));
  }

  setMuted (muted) {
    this.media.setMuted(muted);
  }


  set fullscreen (value) {
    if (value === true) {
      this.view.classList.add('fullScreenLayer');
    } else {
      this.view.classList.remove('fullScreenLayer');
    }
  }

  setSize (width, height) {
    this.view.style.width = width + 'px';
    this.view.style.height = height + 'px';
  }

  setPosition (x, y) {
    this.view.style.left = x+'px';
    this.view.style.top = y + 'px';
  }

  getSetting (path) {
    const cascadingSceneSettingValue = MegaScreen.globals.cascadingSettingsService.getSettingIfNotEmpty(
      `content.${this.name}.media.screen.${path}`,
      MegaScreen.globals.appState.scene,
      MegaScreen.globals.dataStore);

    if (isEmpty(cascadingSceneSettingValue)) {
      return this.getCascadingThemeSettingValue(path);
    } else {
      return cascadingSceneSettingValue;
    }
  }

  getCascadingThemeSettingValue (path) {
    let currentTheme = MegaScreen.globals.themeService.getCurrentScreenTheme();
    // Media settings, product level
    let productSpecificMediaSettingsPath;
    let productDefaultMediaSettingsPath;
    let productName = MegaScreen.globals.sceneService.getScreenProductName(MegaScreen.globals.appState.scene);
    let productLayout =  MegaScreen.globals.extensionService.cascadingGetSceneLayout(MegaScreen.globals.appState.scene, MegaScreen.globals.dataStore.playlist);
    let pathSuffix = this.name === 'cta' ? 'cta.media' : `media.${this.name}`;

    if (MegaScreen.globals.productService.isMultichoiceProduct(productName)) {
      let productID = get(MegaScreen.globals.appState, 'scene.presentation.screen_product.id');
      // Product-specific multichoice media settings (e.g., Face Off settings)
      productSpecificMediaSettingsPath = `design.products.multichoice_products.screen.${productID}.${pathSuffix}`;
      // Default multichoice media settings
      productDefaultMediaSettingsPath = `design.products.multichoice_products.screen.default.${pathSuffix}`;
    } else if (productName === PRODUCTNAME_UGC) {
      if (notEmpty(productLayout)) {
        // UGC layout-specific theme setting.
        // Example productMediaSettingsPath for layout "e" ("Fullscreen Dramatic"):
        //   "design.products.ugc.screen.layout.e.cta.media"
        productSpecificMediaSettingsPath = `design.products.ugc.screen.layout.${productLayout}.${pathSuffix}`;
      }
      // UGC product-default theme setting.
      productDefaultMediaSettingsPath = `design.products.ugc.screen.${pathSuffix}`;
    } else if (MegaScreen.globals.productService.isTitleProduct(productName)) {
      // Media settings for Bulletin, Teaser, etc
      productDefaultMediaSettingsPath = `design.products.title_products.screen.${pathSuffix}`;
    } else if (MegaScreen.globals.productService.isSocialProduct(productName)) {
      // Media settings for Tweet to TV, Facebook to TV, etc
      productDefaultMediaSettingsPath = `design.products.social_products.screen.${pathSuffix}`;
    } else {
      // Media settings for all other products (NOT one of: multichoice, UGC, title, social)
      productDefaultMediaSettingsPath = `design.products.${productName}.screen.${pathSuffix}`;
    }

    // Media settings, state level and default level
    let stateMediaSettingsPath;
    let defaultMediaSettingsPath;

    let stateName = this.appState.state.split('state_')[1];
    stateMediaSettingsPath = `design.states.screen.${stateName}.${pathSuffix}`;
    defaultMediaSettingsPath = `design.default.screen.${pathSuffix}`;

    let settingValue = cascadingGetIfNotEmpty(currentTheme, [
      `${productSpecificMediaSettingsPath}.${path}`,
      `${productDefaultMediaSettingsPath}.${path}`,
      `${stateMediaSettingsPath}.${path}`,
      `${defaultMediaSettingsPath}.${path}`]);

    return settingValue;
  }
}
